import React from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  AlertCircle, AlertTriangle, CheckCircle, ClipboardList, Info, Lightbulb,
  Loader2, Lock, RefreshCw, Sparkles, Star, Unlock, X, Zap,
} from 'lucide-react';
import { EditableField } from './helpers';
import { TabLoader } from './TabLoader';

const AIAnalysisTab = ({
  editData, updateField, canEdit, inspection, aiReport,
  generatingAI, generateAIReport, fetchingRPP, fetchRPP,
  marketPriceResearch, onShowRatingBreakdown, liveProgressData,
}) => {
  return (
    <>
                <TabsContent value="ai-analysis" className="space-y-3 mt-0">
              {!inspection?.id ? <TabLoader message="Loading AI analysis..." /> : (<>
              {/* TOP ROW: Rating + Recommendation + Condition Ratings - Verdict at a Glance */}
              <div className={`rounded-lg border-2 ${aiReport.stale ? 'border-amber-300 bg-amber-50' : 'border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50'}`}>
                <div className="flex items-center justify-between px-3 py-2 border-b border-purple-100">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-sm text-gray-900">AI Analysis</h3>
                    {aiReport.generated && !aiReport.stale && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">Generated</span>}
                    {aiReport.stale && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Needs Update</span>}
                  </div>
                  <Button onClick={generateAIReport} disabled={generatingAI} size="sm" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 h-8 text-xs" data-testid="generate-ai-btn">
                    {generatingAI ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Generating...</> : <><Zap className="h-3 w-3 mr-1" />{aiReport.generated ? 'Regenerate' : 'Generate AI Report'}</>}
                  </Button>
                </div>
                
                {/* Inline verdict: Rating + Recommendation + 4 Condition Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-6 gap-2 p-3">
                  {/* Overall Rating */}
                  <div className="bg-white rounded-lg p-2.5 border">
                    <div className="flex items-center gap-1">
                      <p className="text-[10px] text-gray-500 font-medium">Overall Rating</p>
                      {editData.overall_rating_locked ? (
                        <Lock className="h-3 w-3 text-amber-500" title="Manually set - won't auto-recalculate" />
                      ) : (
                        <Unlock className="h-3 w-3 text-gray-300" title="Auto-calculates from category ratings" />
                      )}
                      <button 
                        onClick={onShowRatingBreakdown}
                        className="ml-1 p-0.5 hover:bg-blue-100 rounded transition-colors"
                        title="View rating breakdown & history"
                      >
                        <Info className="h-3.5 w-3.5 text-blue-500 hover:text-blue-700" />
                      </button>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Input type="number" min="0" max="10" step="0.5" value={editData.overall_rating === 0 ? '' : editData.overall_rating} onChange={(e) => updateField('overall_rating', e.target.value === '' ? 0 : parseFloat(e.target.value))} placeholder="0" className="h-7 w-14 text-center font-bold text-sm" disabled={!canEdit} />
                      <span className="text-xs text-gray-400">/ 10</span>
                      <Star className="h-4 w-4 text-yellow-500" />
                    </div>
                    {editData.overall_rating_locked && (
                      <p className="text-[9px] text-amber-600 mt-1">Locked • Regenerate AI to unlock</p>
                    )}
                  </div>
                  
                  {/* Recommendation */}
                  <div className="bg-white rounded-lg p-2.5 border">
                    <p className="text-[10px] text-gray-500 font-medium">Recommendation</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Switch checked={editData.recommended_to_buy} onCheckedChange={(val) => updateField('recommended_to_buy', val)} disabled={!canEdit} className="scale-90" />
                      <span className={`text-xs font-medium ${editData.recommended_to_buy ? 'text-green-600' : 'text-red-600'}`}>
                        {editData.recommended_to_buy ? 'Buy' : 'Don\'t Buy'}
                      </span>
                    </div>
                  </div>
                  
                  {/* 4 Condition Rating Cards - Inline with Editable Dropdown */}
                  {['engine', 'exterior', 'interior', 'transmission'].map(key => {
                    const labels = { engine: 'Engine', exterior: 'Exterior', interior: 'Interior', transmission: 'Transmission' };
                    const condition = (editData[`${key}_condition`] || 'PENDING').toUpperCase();
                    const colors = {
                      'VERY GOOD': 'bg-green-50 border-green-300 text-green-700',
                      GOOD: 'bg-emerald-50 border-emerald-300 text-emerald-700',
                      AVERAGE: 'bg-yellow-50 border-yellow-300 text-yellow-700',
                      BAD: 'bg-red-50 border-red-300 text-red-700',
                      PENDING: 'bg-gray-50 border-gray-300 text-gray-500',
                    };
                    const fieldKey = `${key}_condition`;
                    return (
                      <div key={key} className={`rounded-lg p-2.5 border ${colors[condition] || 'bg-gray-50 border-gray-300 text-gray-500'}`}>
                        <p className="text-[10px] text-gray-500 font-medium">{labels[key]}</p>
                        {canEdit ? (
                          <select 
                            value={condition}
                            onChange={(e) => updateField(fieldKey, e.target.value)}
                            className={`text-sm font-bold mt-0.5 bg-transparent border-none outline-none cursor-pointer w-full ${colors[condition]?.split(' ').slice(-1)[0] || 'text-gray-500'}`}
                            data-testid={`${key}-condition-select`}
                          >
                            <option value="PENDING">PENDING</option>
                            <option value="VERY GOOD">VERY GOOD</option>
                            <option value="GOOD">GOOD</option>
                            <option value="AVERAGE">AVERAGE</option>
                            <option value="BAD">BAD</option>
                          </select>
                        ) : (
                          <p className="text-sm font-bold mt-0.5">{condition}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* TWO-COLUMN LAYOUT: RPP (Left Narrow) + Assessment Summary (Right Wide) */}
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
                {/* LEFT: RPP - Recommended Purchase Price (2/5 width) */}
                <div className="lg:col-span-2 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-3 border-2 border-green-200 flex flex-col">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-sm text-gray-900">Recommended Purchase Price</h3>
                      {marketPriceResearch?.market_average > 0 && (
                        <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">{marketPriceResearch.sources_count} sources</span>
                      )}
                    </div>
                    <Button onClick={fetchRPP} disabled={fetchingRPP || !inspection?.vehicle_make || !inspection?.vehicle_model} size="sm" className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 h-7 text-xs" data-testid="fetch-rpp-btn">
                      {fetchingRPP ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Fetching...</> : <><RefreshCw className="h-3 w-3 mr-1" />Fetch Prices</>}
                    </Button>
                  </div>
                  
                  {/* Price Range Input */}
                  <div className="bg-white rounded-lg p-2.5 border mb-2">
                    <p className="text-[10px] text-gray-500 mb-1.5">Price Range</p>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-sm">Rs.</span>
                      <Input type="number" value={editData.market_value_min === 0 ? '' : editData.market_value_min} onChange={(e) => updateField('market_value_min', e.target.value === '' ? 0 : parseFloat(e.target.value))} className="h-8 text-sm font-semibold" placeholder="Min" disabled={!canEdit} />
                      <span className="text-gray-400 text-xs">to</span>
                      <Input type="number" value={editData.market_value_max === 0 ? '' : editData.market_value_max} onChange={(e) => updateField('market_value_max', e.target.value === '' ? 0 : parseFloat(e.target.value))} className="h-8 text-sm font-semibold" placeholder="Max" disabled={!canEdit} />
                    </div>
                    {editData.market_value_min > 0 && editData.market_value_max > 0 && (
                      <p className="text-sm font-bold text-green-700 mt-1.5">Rs.{(editData.market_value_min / 100000).toFixed(2)} - Rs.{(editData.market_value_max / 100000).toFixed(2)} Lakh</p>
                    )}
                  </div>
                  
                  {/* Market Research Results */}
                  {marketPriceResearch?.market_average > 0 ? (
                    <div className="flex-1 space-y-2">
                      <div className="bg-white rounded-lg p-2 border flex items-center justify-between">
                        <span className="text-xs text-gray-600">Market Average</span>
                        <span className="text-sm font-bold text-gray-800">Rs.{(marketPriceResearch.market_average / 100000).toFixed(2)}L</span>
                      </div>
                      {marketPriceResearch.sources?.length > 0 && (
                        <div className="grid grid-cols-2 gap-1.5">
                          {marketPriceResearch.sources.map((source, idx) => {
                            const getSourceStyle = (name) => {
                              switch(name) {
                                case 'CarDekho': return 'bg-orange-50 border-orange-200 text-orange-700';
                                case 'CarWale': return 'bg-blue-50 border-blue-200 text-blue-700';
                                case 'Cars24': return 'bg-yellow-50 border-yellow-200 text-yellow-700';
                                case 'Spinny': return 'bg-purple-50 border-purple-200 text-purple-700';
                                case 'OLX': return 'bg-green-50 border-green-200 text-green-700';
                                default: return 'bg-gray-50 border-gray-200 text-gray-700';
                              }
                            };
                            return (
                              <div key={idx} className={`flex items-center justify-between rounded px-2 py-1.5 border text-xs ${getSourceStyle(source.source)}`}>
                                <span className="font-medium">{source.source}</span>
                                <span className="font-bold">Rs.{(source.price / 100000).toFixed(2)}L</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-2.5 border flex items-center gap-2 text-xs text-amber-700">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      <span>{inspection?.vehicle_make && inspection?.vehicle_model ? 'Click "Fetch Prices" to get market data' : 'Vehicle make/model needed'}</span>
                    </div>
                  )}
                </div>
                
                {/* RIGHT: Section-wise Assessment (3/5 width) */}
                <div className="lg:col-span-3 bg-slate-50 rounded-lg p-3 border flex flex-col">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-sm text-gray-900 flex items-center gap-2">
                      <ClipboardList className="h-4 w-4 text-blue-600" />
                      AI Assessment
                    </h3>
                    {inspection?.ai_insights?.ai_generated && (
                      <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full flex items-center gap-1">
                        <Sparkles className="h-3 w-3" />AI Generated
                      </span>
                    )}
                  </div>
                  
                  {/* Overall assessment */}
                  {inspection?.ai_insights?.assessment_summary?.overall && (
                    <div className="bg-white rounded-lg p-2 border-l-4 border-l-blue-500 mb-2">
                      <p className="text-xs text-gray-700">{inspection.ai_insights.assessment_summary.overall}</p>
                    </div>
                  )}
                  
                  {/* Section cards in 2-col grid */}
                  {inspection?.ai_insights?.assessment_summary && typeof inspection.ai_insights.assessment_summary === 'object' && (
                    <div className="grid grid-cols-2 gap-1.5 mb-2">
                      {inspection.ai_insights.assessment_summary.engine_and_mechanical && (
                        <div className="bg-white rounded p-2 border text-xs">
                          <p className="font-semibold text-gray-500 text-[10px] uppercase mb-0.5">Engine & Mech.</p>
                          <p className="text-gray-700 line-clamp-3">{inspection.ai_insights.assessment_summary.engine_and_mechanical}</p>
                        </div>
                      )}
                      {inspection.ai_insights.assessment_summary.exterior_body && (
                        <div className="bg-white rounded p-2 border text-xs">
                          <p className="font-semibold text-gray-500 text-[10px] uppercase mb-0.5">Exterior Body</p>
                          <p className="text-gray-700 line-clamp-3">{inspection.ai_insights.assessment_summary.exterior_body}</p>
                        </div>
                      )}
                      {inspection.ai_insights.assessment_summary.interior_comfort && (
                        <div className="bg-white rounded p-2 border text-xs">
                          <p className="font-semibold text-gray-500 text-[10px] uppercase mb-0.5">Interior</p>
                          <p className="text-gray-700 line-clamp-3">{inspection.ai_insights.assessment_summary.interior_comfort}</p>
                        </div>
                      )}
                      {inspection.ai_insights.assessment_summary.safety_systems && (
                        <div className="bg-white rounded p-2 border text-xs">
                          <p className="font-semibold text-gray-500 text-[10px] uppercase mb-0.5">Safety</p>
                          <p className="text-gray-700 line-clamp-3">{inspection.ai_insights.assessment_summary.safety_systems}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Custom Summary */}
                  <div className="flex-1">
                    <EditableField label="Custom Summary" value={editData.assessment_summary} onChange={(val) => updateField('assessment_summary', val)} type="textarea" placeholder="Enter vehicle assessment summary..." disabled={!canEdit} />
                  </div>
                </div>
              </div>
              
              {/* BOTTOM ROW: Highlights + Concerns + Risks + Recommendations in 2x2 grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Key Highlights */}
                <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                  <p className="text-xs font-semibold text-green-800 mb-2 flex items-center gap-1.5">
                    <CheckCircle className="h-3.5 w-3.5" />Key Highlights
                  </p>
                  <div className="space-y-1">
                    {inspection?.ai_insights?.key_highlights?.length > 0 && (
                      <div className="space-y-0.5 mb-2">
                        {inspection.ai_insights.key_highlights.map((highlight, idx) => (
                          <div key={`ai-${idx}`} className="flex items-start gap-1.5 text-xs text-green-700">
                            <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                            <span>{highlight}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {(editData.key_highlights || []).map((highlight, idx) => (
                      <div key={idx} className="flex items-center gap-1.5">
                        <Input value={highlight} onChange={(e) => { const n = [...editData.key_highlights]; n[idx] = e.target.value; updateField('key_highlights', n); }} className="h-7 text-xs" disabled={!canEdit} />
                        {canEdit && <Button variant="ghost" size="sm" onClick={() => updateField('key_highlights', editData.key_highlights.filter((_, i) => i !== idx))} className="h-7 w-7 p-0 text-red-500"><X className="h-3 w-3" /></Button>}
                      </div>
                    ))}
                    {canEdit && <Button variant="outline" size="sm" onClick={() => updateField('key_highlights', [...(editData.key_highlights || []), ''])} className="text-[10px] h-6">+ Add</Button>}
                  </div>
                </div>
                
                {/* Concerns + Risks combined */}
                <div className="space-y-3">
                  {inspection?.ai_insights?.concerns?.length > 0 && (
                    <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                      <p className="text-xs font-semibold text-red-800 mb-1.5 flex items-center gap-1.5">
                        <AlertCircle className="h-3.5 w-3.5" />Concerns ({inspection.ai_insights.concerns.length})
                      </p>
                      <div className="space-y-0.5">
                        {inspection.ai_insights.concerns.map((concern, idx) => (
                          <div key={idx} className="flex items-start gap-1.5 text-xs text-red-700">
                            <span className="text-red-500 mt-0.5 flex-shrink-0">!</span>
                            <span>{concern}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {inspection?.ai_insights?.risk_factors?.length > 0 && (
                    <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                      <p className="text-xs font-semibold text-amber-800 mb-1.5 flex items-center gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5" />Risk Factors ({inspection.ai_insights.risk_factors.length})
                      </p>
                      <div className="space-y-0.5">
                        {inspection.ai_insights.risk_factors.map((risk, idx) => (
                          <div key={idx} className="flex items-start gap-1.5 text-xs text-amber-700">
                            <span className="text-amber-500 mt-0.5 flex-shrink-0">!</span>
                            <span>{risk}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Recommendations - Full Width Compact */}
              {inspection?.ai_insights?.recommendations?.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <p className="text-xs font-semibold text-blue-800 mb-1.5 flex items-center gap-1.5">
                    <Lightbulb className="h-3.5 w-3.5" />Recommendations
                  </p>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-0.5">
                    {inspection.ai_insights.recommendations.map((rec, idx) => (
                      <div key={idx} className="flex items-start gap-1.5 text-xs text-blue-700">
                        <span className="text-blue-500 mt-0.5 flex-shrink-0">→</span>
                        <span>{rec}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              </>)}
            </TabsContent>
            

    </>
  );
};

export default AIAnalysisTab;
